import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import HeroSection from "../components/landing/hero-section";
import { Button } from "../components/ui/button";
import {
  TrendingUp,
  ArrowRight,
  MapPin,
  Bed,
  Bath,
  Square,
  Heart,
  ArrowUpRight,
  Sparkles
} from "lucide-react";
import coeurImg from "../assets/img3d/coeur.png";
import { motion, useScroll, useTransform, useMotionTemplate, useMotionValue } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- COMPOSANT BACKGROUND : GRILLE & GRAIN (L'effet "Oeuvre d'art") ---
const ArchitecturalBackground = () => {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* 1. Texture de Grain (Papier Luxe) */}
      <div className="absolute inset-0 opacity-[0.03] z-0 mix-blend-overlay" 
           style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} 
      />

      {/* 2. Grille Architecturale Subtile */}
      <div className="absolute inset-0 z-0 opacity-[0.4]"
           style={{ 
             backgroundImage: 'linear-gradient(#FF9F68 1px, transparent 1px), linear-gradient(to right, #FF9F68 1px, transparent 1px)', 
             backgroundSize: '80px 80px',
             maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
             WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)'
           }} 
      />
    </div>
  );
};

// --- COMPOSANT EXCLUSIF : CARTE VEDETTE ---
const FeaturedPropertyCard = ({ property, onClick }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-CH", {
      style: "currency",
      currency: "CHF",
      minimumFractionDigits: 0,
    }).format(price).replace("F CFA", "FCFA");
  };

  return (
    <div
      onClick={onClick}
      className="group relative bg-white/80 backdrop-blur-sm rounded-[2rem] overflow-hidden border border-white/50 shadow-sm hover:shadow-[0_20px_50px_-12px_rgba(255,159,104,0.2)] transition-all duration-500 cursor-pointer h-full flex flex-col z-10"
    >
      {/* Image Container */}
      <div className="relative h-80 overflow-hidden">
        <div className="absolute inset-0 bg-gray-100 animate-pulse" />
        <img
          src={property.images?.[0] || "/placeholder-property.jpg"}
          alt={property.title}
          className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-80" />
        
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-[10px] font-bold text-gray-900 uppercase tracking-widest shadow-sm border border-white/50">
            {property.transactionType === "SALE" ? "Vente" : "Location"}
          </span>
        </div>

        <button className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/20 hover:bg-white hover:text-red-500 transition-all active:scale-90">
          <Heart className="w-5 h-5" />
        </button>

        <div className="absolute bottom-5 left-5">
          <span className="text-3xl font-extrabold text-white drop-shadow-lg tracking-tight">
            {formatPrice(property.price)}
            {property.transactionType === "RENT" && (
              <span className="text-sm font-medium text-white/80 ml-1">/mois</span>
            )}
          </span>
        </div>
      </div>

      {/* Content Body */}
      <div className="p-6 flex flex-col flex-1 relative">
        {/* Lueur subtile en arrière plan de la card au hover */}
        <div className="absolute -top-20 left-0 right-0 h-20 bg-gradient-to-b from-transparent to-white/50 group-hover:to-orange-50/30 transition-colors duration-500" />
        
        <div className="mb-6 relative z-10">
          <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1 group-hover:text-[#FF9F68] transition-colors">
            {property.title}
          </h3>
          <div className="flex items-center text-gray-500 text-sm">
            <MapPin className="w-4 h-4 mr-1.5 text-[#FF9F68]" />
            <span className="line-clamp-1">{property.city}</span>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6 relative z-10">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-50/80 border border-gray-100 text-gray-600 text-sm font-medium">
            <Bed className="w-4 h-4 text-gray-400" />
            <span>{property.bedrooms || 0}</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-50/80 border border-gray-100 text-gray-600 text-sm font-medium">
            <Bath className="w-4 h-4 text-gray-400" />
            <span>{property.bathrooms || 0}</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-50/80 border border-gray-100 text-gray-600 text-sm font-medium">
            <Square className="w-4 h-4 text-gray-400" />
            <span>{property.area} m²</span>
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100 relative z-10">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wide group-hover:text-[#FF9F68] transition-colors">
            Découvrir le bien
          </span>
          <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-900 group-hover:bg-[#FF9F68] group-hover:text-white transition-all duration-300 shadow-sm group-hover:shadow-md group-hover:translate-x-1">
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300" />
          </div>
        </div>
      </div>
    </div>
  );
};

// --- HOME PAGE PRINCIPALE ---
const HomePage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Parallaxe pour les "Lumières" d'arrière plan
  const { scrollYProgress } = useScroll();
  const yLight1 = useTransform(scrollYProgress, [0, 1], [0, 400]);
  const yLight2 = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const opacityLight = useTransform(scrollYProgress, [0, 0.2, 0.8], [0, 1, 0]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    fetchFeaturedProperties();
  }, []);

  const fetchFeaturedProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties?featured=true&limit=6`);
      setFeaturedProperties(response.data);
    } catch (error) {
      console.error("Error fetching featured properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (searchData) => {
    const params = new URLSearchParams();
    if (searchData.location) params.append("city", searchData.location);
    if (searchData.action) params.append("type", searchData.action === "buy" ? "sale" : searchData.action);
    if (searchData.propertyType) params.append("propertyType", searchData.propertyType);
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div className="w-full bg-[#FAFAFA] font-sans selection:bg-[#FF9F68] selection:text-white overflow-hidden relative">
      
      {/* --- BACKGROUND VIVANT (Spotlights mouvants) --- */}
      <motion.div 
        style={{ y: yLight1, opacity: opacityLight }}
        className="absolute top-[20%] -left-[10%] w-[800px] h-[800px] bg-gradient-to-r from-orange-200/30 to-purple-200/30 rounded-full blur-[120px] pointer-events-none mix-blend-multiply z-0"
      />
      <motion.div 
        style={{ y: yLight2, opacity: opacityLight }}
        className="absolute top-[50%] -right-[10%] w-[600px] h-[600px] bg-gradient-to-l from-blue-200/30 to-cyan-200/30 rounded-full blur-[100px] pointer-events-none mix-blend-multiply z-0"
      />

      <main className="w-full relative">
        
        {/* --- 1. HERO SECTION --- */}
        <div id="top" className="w-full relative z-20" ref={heroRef}>
          <HeroSection onSearchSubmit={handleSearchSubmit} />
          {/* Transition Gradient Améliorée */}
          <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-[#FAFAFA] via-[#FAFAFA]/80 to-transparent pointer-events-none" />
        </div>

        {/* --- 2. SECTION VEDETTE (ARTISTIC) --- */}
        <section className="relative z-30 -mt-20 pb-32 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Ajout de la grille architecturale en fond de section */}
          <ArchitecturalBackground />

          {/* En-tête de section */}
          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 px-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl"
            >
              <div className="flex items-center gap-2 mb-6">
              </div>
              
              <h2 className="text-5xl font-black text-gray-900 tracking-tight mb-4 flex items-center gap-4 flex-wrap">
                <span>Nos coups de</span>
                <div className="relative inline-block">
                   {/* Halo derrière le coeur */}
                   <div className="absolute inset-0 bg-red-100 rounded-full blur-xl opacity-50 animate-pulse"></div>
                   
                   {/* --- MODIFICATION ICI : Animation subtile du coeur --- */}
                   <motion.img
                    src={coeurImg}
                    alt="coup de cœur"
                    // On garde les classes de base, mais on retire les classes Tailwind de transformation/hover
                    className="h-14 w-14 object-contain drop-shadow-lg relative z-10"
                    // État initial (légèrement penché)
                    initial={{ rotate: -12, y: 0 }}
                    // Animation continue (flottement lent + léger balancement)
                    animate={{ 
                      y: [0, -8, 0],
                      rotate: [-12, -8, -12]
                    }}
                    // Interaction au survol (se redresse et grossit légèrement)
                    whileHover={{ 
                      rotate: 0, 
                      scale: 1.1,
                      transition: { duration: 0.3, ease: "easeOut" }
                    }}
                    // Configuration de l'animation continue
                    transition={{
                      duration: 4, // Très lent pour être subtil
                      repeat: Infinity,
                      repeatType: "reverse",
                      ease: "easeInOut"
                    }}
                  />
                  {/* --- FIN DE LA MODIFICATION --- */}

                </div>
                <span className="relative">
                  <span className="relative z-10">du moment</span>
                  {/* Surlignage effet marqueur */}
                  <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#FF9F68]/30 -z-0" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                  </svg>
                </span>
              </h2>

              <p className="text-xl text-gray-500 font-light leading-relaxed max-w-lg">
                L'immobilier n'est pas qu'une question de murs. 
                <br/>
                C'est une question d'art de vivre. Découvrez notre sélection.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden md:block"
            >
              <Button
                onClick={() => navigate("/properties")}
                className="h-16 px-10 rounded-full bg-white text-gray-900 border border-gray-200 hover:border-[#FF9F68] hover:bg-gray-50 shadow-xl shadow-gray-200/50 hover:shadow-orange-100 transition-all group"
              >
                <span className="font-bold text-base mr-2">Explorer la collection</span>
                <div className="bg-gray-100 rounded-full p-2 group-hover:bg-[#FF9F68] group-hover:text-white transition-colors">
                    <ArrowRight className="h-4 w-4" />
                </div>
              </Button>
            </motion.div>
          </div>

          {/* Grille de Cartes Premium */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 relative z-10">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[500px] bg-white rounded-[2rem] animate-pulse shadow-sm border border-gray-100" />
              ))}
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 relative z-10">
              {featuredProperties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 80 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    delay: index * 0.15,
                    duration: 0.8,
                    type: "spring",
                    bounce: 0.3
                  }}
                >
                  <FeaturedPropertyCard
                    property={property}
                    onClick={() => navigate(`/properties/${property.id}`)}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              className="text-center py-32 relative z-10"
            >
              <div className="bg-white/50 backdrop-blur-xl rounded-[3rem] border border-white/60 p-16 max-w-2xl mx-auto shadow-2xl shadow-orange-100/50">
                  <div className="w-24 h-24 bg-gradient-to-tr from-orange-100 to-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner ring-4 ring-white">
                    <TrendingUp className="h-10 w-10 text-[#FF9F68]" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Le calme avant la tempête
                  </h3>
                  <p className="text-gray-500 mb-10 text-lg leading-relaxed">
                    Nos équipes dénichent les perles rares. <br/>Soyez le premier à publier votre bien d'exception.
                  </p>
                  <Button
                    onClick={() => navigate("/add-property")}
                    className="bg-gradient-to-r from-[#FF9F68] to-[#FFB085] hover:from-[#E67E4F] hover:to-[#FF9F68] text-white px-12 h-16 rounded-2xl text-lg font-bold shadow-xl shadow-orange-200 hover:shadow-orange-300 transition-all transform hover:-translate-y-1"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Déposer une annonce
                  </Button>
              </div>
            </motion.div>
          )}

          {/* Bouton Mobile */}
          <div className="mt-16 text-center md:hidden relative z-10">
            <Button
              onClick={() => navigate("/properties")}
              variant="outline"
              className="w-full h-14 rounded-2xl border-gray-300 text-gray-900 font-semibold bg-white/50 backdrop-blur-md"
            >
              Voir toutes les offres
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
